import React from 'react';
import { Platform, View } from 'react-native';
import { styled } from 'dripsy';

import { BaseText } from '../common/BaseText';
import { Icon } from '../common/Icon';
import { Touchable } from '../common/Touchable';
import { ColorMapType } from '@types';
import { ToastTypeEnum } from '../../services/ToastService';

interface IToastStyleProps {
  type: ToastTypeEnum;
}
interface ITitlesStyleProps extends IToastStyleProps {
  hasDescription: boolean;
}

const backgroundColorMap: ColorMapType<ToastTypeEnum> = {
  [ToastTypeEnum.Success]: '$primary80',
  [ToastTypeEnum.Error]: '$error80',
  [ToastTypeEnum.Warning]: '$waring80',
  [ToastTypeEnum.Info]: '$info80',
};

const titleColorMap: ColorMapType<ToastTypeEnum> = {
  [ToastTypeEnum.Success]: '$primary300',
  [ToastTypeEnum.Error]: '$error300',
  [ToastTypeEnum.Warning]: '$waring300',
  [ToastTypeEnum.Info]: '$info300',
};

const iconColorMap = titleColorMap;

const iconNamesMap: Record<ToastTypeEnum, string> = {
  [ToastTypeEnum.Success]: 'tick-square-bold',
  [ToastTypeEnum.Error]: 'close-square-bold',
  [ToastTypeEnum.Warning]: 'danger-bold',
  [ToastTypeEnum.Info]: 'info-bold',
};

const Container = styled(View)((props: IToastStyleProps) => ({
  backgroundColor: backgroundColorMap[props.type],
  ...Platform.select({
    default: {
      width: '90%',
    },
    web: {
      minWidth: ['90%', 300],
      alignSelf: ['center', 'flex-end'],
      marginRight: ['$0', '$9'],
    },
  }),
  paddingTop: '$4',
  paddingLeft: '$6',
  paddingRight: '$5',
  paddingBottom: '$4',
  flexDirection: 'row',
  borderRadius: 2,
}));

const ContentContainer = styled(View)({
  flex: 1,
});

const TitleContainer = styled(View)({
  flexDirection: 'row',
  justifyContent: 'space-between',
});

const Title = styled(BaseText)((props: ITitlesStyleProps) => ({
  color: props.hasDescription
    ? titleColorMap[props.type]
    : '$heading800',
  variant: 'text.medium',
}));

const Description = styled(BaseText)({
  color: '$text400',
  variant: 'text.small',
  marginTop: '$3',
});

const ToastIcon = styled(Icon)({
  marginRight: '$4',
});

export interface IToastProps {
  type?: ToastTypeEnum;
  title: string;
  description?: string;
  canBeHidden?: boolean;
  onHide: () => void;
}

export function Toast({
  title,
  description,
  onHide,
  type = ToastTypeEnum.Success,
  canBeHidden,
}: IToastProps) {
  return (
    <Container type={type}>
      <ToastIcon
        name={iconNamesMap[type]}
        color={iconColorMap[type]}
        size={20}
      />
      <ContentContainer>
        <TitleContainer>
          <Title hasDescription={!!description} type={type}>
            {title}
          </Title>
          {canBeHidden && (
            <Touchable borderless onPress={onHide}>
              <Icon name="close-light" size={20} />
            </Touchable>
          )}
        </TitleContainer>
        {!!description && <Description>{description}</Description>}
      </ContentContainer>
    </Container>
  );
}
